import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Plus,
  Grid3X3,
  List
} from 'lucide-react';
import { shipmentService } from '../services/apiService';
import { formatDateForApi, getWeekDays } from '../utils/dateUtils';
import { getShipmentDisplayInfo } from '../utils/shipmentUtils';
import { SHIPMENT_FIELDS, SHIPMENT_STATUS, USER_TYPES } from '../constants';

// Helper function to get week number
const getWeekNumber = (date) => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

// Helper function to get week days starting from Monday
const getWeekDaysStartingMonday = (date) => {
  const week = [];
  const startDate = new Date(date);
  
  // Get Monday of the week
  const day = startDate.getDay();
  const diff = startDate.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
  startDate.setDate(diff);

  for (let i = 0; i < 7; i++) {
    const weekDay = new Date(startDate);
    weekDay.setDate(startDate.getDate() + i);
    week.push(weekDay);
  }
  return week;
};

// Helper function to format time
const formatTime = (hour, minute) => {
  if (!hour || hour === '0') return '';
  const h = String(hour).padStart(2, '0');
  const m = String(minute || '0').padStart(2, '0');
  return `${h}:${m}`;
};

// Helper function to get status colors for events
// Helper function to get status colors for events
const getEventColors = (status) => {
  switch (status) {
    case 0: return 'bg-blue-100 border-l-4 border-blue-500 text-blue-800'; // Chờ xác nhận
    case 1: return 'bg-green-100 border-l-4 border-green-500 text-green-800'; // Đã nhận chuyến
    case 2: return 'bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800'; // Đang giao
    case 5: return 'bg-purple-100 border-l-4 border-purple-500 text-purple-800'; // Hoàn thành
    case 9: return 'bg-red-100 border-l-4 border-red-500 text-red-800'; // Hủy
    default: return 'bg-gray-100 border-l-4 border-gray-300 text-gray-700';
  }
};

// Helper function to get status badge
const getStatusBadge = (status) => {
  const statusText = {
    0: 'Chờ xác nhận',
    1: 'Đã nhận',
    2: 'Đang giao',
    5: 'Hoàn thành',
    9: 'Hủy'
  };
  
  const statusColors = {
    0: 'bg-blue-500 text-white',
    1: 'bg-green-500 text-white',
    2: 'bg-yellow-500 text-black',
    5: 'bg-purple-500 text-white',
    9: 'bg-red-500 text-white'
  };
  
  return {
    text: statusText[status] || 'N/A',
    color: statusColors[status] || 'bg-gray-400 text-white'
  };
};

// Helper function to get delivery times array
const getDeliveryTimes = (shipment) => {
  const times = [];
  for (let i = 1; i <= 4; i++) {
    const hour = shipment[`giogiao${i}`];
    const minute = shipment[`phutgiao${i}`];
    const time = formatTime(hour, minute);
    if (time) {
      times.push(time);
    }
  }
  return times;
};

const getCustomersDisplay = (shipment) => {
  // const parts = [];
  
  // // Khách hàng chính
  // if (shipment.khachhang && shipment.khachhang.trim()) {
  //   parts.push(shipment.khachhang.trim());
  // }
  
  // // Khách hàng nhận (điểm lấy hàng)
  // if (shipment.khachhangnhan && shipment.khachhangnhan.trim()) {
  //   parts.push(shipment.khachhangnhan.trim());
  // }
  
  // // Các điểm giao hàng
  // for (let i = 1; i <= 4; i++) {
  //   const customer = shipment[`khachhang${i}`];
  //   if (customer && customer.trim()) {
  //     parts.push(customer.trim());
  //   }
  // }
  return shipment.khachhang;
  //return parts.length > 0 ? parts.join(' → ') : 'N/A';
};

const getAddressesDisplay = (shipment) => {
  const addresses = [];
  
  // Địa chỉ lấy hàng
  if (shipment.diachinhan && shipment.diachinhan.trim()) {
    addresses.push(`Lấy: ${shipment.diachinhan.trim()}`);
  } else if (shipment.khachhangnhan && shipment.khachhangnhan.trim()) {
    // Nếu không có địa chỉ cụ thể, hiển thị tên khách hàng nhận
    addresses.push(`Lấy: ${shipment.khachhangnhan.trim()}`);
  }
  
  // Các địa chỉ giao hàng
  for (let i = 1; i <= 4; i++) {
    const address = shipment[`diachigiao${i}`];
    const customer = shipment[`khachhang${i}`];
    
    if (address && address.trim()) {
      addresses.push(`Giao ${i}: ${address.trim()}`);
    } else if (customer && customer.trim()) {
      // Nếu không có địa chỉ cụ thể, hiển thị tên khách hàng
      addresses.push(`Giao ${i}: ${customer.trim()}`);
    }
  }
  
  return addresses.length > 0 ? addresses : ['Chưa có địa chỉ'];
};

// Helper function to get customers array
const getCustomersArray = (shipment) => {
  const customers = [];
  if (shipment.khachhang && shipment.khachhang.trim()) {
    customers.push(shipment.khachhang.trim());
  }
  for (let i = 1; i <= 4; i++) {
    const customer = shipment[`khachhang${i}`];
    if (customer && customer.trim()) {
      customers.push(customer.trim());
    }
  }
  return customers;
};

// Helper function to get delivery addresses array
const getDeliveryAddressesArray = (shipment) => {
  const addresses = [];
  if (shipment.diachinhan && shipment.diachinhan.trim()) {
    addresses.push({ type: 'pickup', address: shipment.diachinhan.trim() });
  }
  for (let i = 1; i <= 4; i++) {
    const address = shipment[`diachigiao${i}`];
    if (address && address.trim()) {
      addresses.push({ type: 'delivery', address: address.trim() });
    }
  }
  return addresses;
};

// Helper function to calculate event height based on content
const calculateEventHeight = (shipment) => {
  const baseHeight = 60; // Base height for basic info
  const timeHeight = 20; // Height for time display
  const locationHeight = 40; // Height for location info (can be 2 lines)
  
  const displayInfo = getShipmentDisplayInfo(shipment);
  const pickupTime = formatTime(shipment.gionhan1, shipment.phutnhan1);
  const deliveryTime = formatTime(shipment.giogiao1, shipment.phutgiao1);
  
  let totalHeight = baseHeight;
  
  // Add height for time if present
  if (pickupTime || deliveryTime) {
    totalHeight += timeHeight;
  }
  
  // Add height for location info
  totalHeight += locationHeight;
  
  return totalHeight;
};

// Helper function to calculate day height based on events
const calculateDayHeight = (dayShipments) => {
  if (dayShipments.length === 0) {
    return 150; // Minimum height for empty day
  }
  
  const headerHeight = 60; // Height of day header
  const padding = 16; // Padding inside day container
  const eventSpacing = 8; // Space between events
  
  const totalEventHeight = dayShipments.reduce((total, shipment) => {
    return total + calculateEventHeight(shipment);
  }, 0);
  
  const totalSpacing = (dayShipments.length - 1) * eventSpacing;
  
  return headerHeight + padding + totalEventHeight + totalSpacing;
};

const WeeklyCalendarView = ({ userData, onSelectShipment, onCreateShipment }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Refs for scrolling
  const scrollContainerRef = useRef(null);
  const todayElementRef = useRef(null);

  useEffect(() => {
    loadShipments();
  }, [currentWeek, userData]);

  // Auto-scroll to today when component mounts or week changes
  useEffect(() => {
    const scrollToToday = () => {
      if (todayElementRef.current && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const todayElement = todayElementRef.current;
        
        console.log('🎯 Scrolling to today element:', todayElement);
        
        // Calculate the scroll position to center today's element
        const containerRect = container.getBoundingClientRect();
        const todayRect = todayElement.getBoundingClientRect();
        
        const scrollTop = container.scrollTop + todayRect.top - containerRect.top - (containerRect.height / 2) + (todayRect.height / 2);
        
        console.log('📍 Scroll calculation:', {
          containerHeight: containerRect.height,
          todayTop: todayRect.top,
          containerTop: containerRect.top,
          currentScrollTop: container.scrollTop,
          targetScrollTop: Math.max(0, scrollTop)
        });
        
        // Smooth scroll to today
        container.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      } else {
        console.log('❌ Missing refs for scroll:', {
          todayRef: !!todayElementRef.current,
          containerRef: !!scrollContainerRef.current
        });
      }
    };

    // Delay scroll to ensure DOM is rendered
    const timer = setTimeout(() => {
      console.log('⏰ Triggering scroll to today...');
      scrollToToday();
    }, 100);

    return () => clearTimeout(timer);
  }, [currentWeek, shipments, loading]); // Trigger when week changes or data loads

  const loadShipments = async () => {
    if (!userData?.idLaiXe && userData?.idLaiXe !== 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Get the week dates - use custom function to ensure Monday start
      const weekDates = getWeekDaysStartingMonday(currentWeek);
      const firstDay = weekDates[0];
      const lastDay = weekDates[6];
      
      console.log('📅 Loading shipments for week:', {
        currentWeek: currentWeek.toDateString(),
        firstDay: firstDay.toDateString(),
        lastDay: lastDay.toDateString(),
        weekDates: weekDates.map(d => d.toDateString())
      });
      
      const tungay = formatDateForApi(firstDay);
      const denngay = formatDateForApi(lastDay);
      
      const result = await shipmentService.getShipmentsForDriver(
        userData?.type === 2 ? 0 : userData.idLaiXe,
        tungay,
        denngay
      );
      
      if (result.success && result.data) {
        setShipments(result.data);
      } else {
        setError(result.message || 'Không thể tải dữ liệu chuyến xe');
        setShipments([]);
      }
    } catch (err) {
      setError('Có lỗi xảy ra khi tải dữ liệu');
      setShipments([]);
    } finally {
      setLoading(false);
    }
  };

  const getShipmentsForDate = (date) => {
  const dateStr = formatDateForApi(date);
  return shipments.filter(shipment => 
    shipment.ngaynhan === dateStr // Sử dụng trực tiếp field từ API response
  );
};

  const handlePreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(currentWeek.getDate() + 7);
    setCurrentWeek(newDate);
  };

  const handleCurrentWeek = () => {
    setCurrentWeek(new Date());
    // Auto-scroll will be triggered by useEffect
  };

  const weekDates = getWeekDaysStartingMonday(currentWeek);
  const weekNumber = getWeekNumber(currentWeek);
  const today = new Date();
  
  // console.log('🗓️ Week info:', {
  //   today: today.toDateString(),
  //   currentWeek: currentWeek.toDateString(),
  //   weekDates: weekDates.map((d, i) => ({
  //     index: i,
  //     date: d.toDateString(),
  //     isToday: d.toDateString() === today.toDateString()
  //   }))
  // });
  const monthNames = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
    'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
  ];
  const dayNames = ['Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy', 'Chủ nhật'];
  const dayNamesShort = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

  // Calculate max height needed for this week
  const maxDayHeight = Math.max(
    ...weekDates.map(date => {
      const dayShipments = getShipmentsForDate(date);
      return calculateDayHeight(dayShipments);
    })
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải lịch...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Fixed Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="p-4">
          {/* Top navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePreviousWeek}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              
              <div className="text-center">
                <h1 className="text-lg font-semibold text-gray-900">
                  Tuần {weekNumber} - {monthNames[currentWeek.getMonth()]} {currentWeek.getFullYear()}
                </h1>
                <button
                  onClick={handleCurrentWeek}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Tuần hiện tại
                </button>
              </div>
              
              <button
                onClick={handleNextWeek}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Calendar Content */}
      <div className="flex-1 overflow-y-auto pb-20" ref={scrollContainerRef}>
        <div className="p-2 md:p-4 pb-8">
          {/* Desktop: 7-column grid */}
          <div className="hidden md:grid md:grid-cols-7 gap-2">
            {weekDates.map((date, index) => {
              const dayShipments = getShipmentsForDate(date);
              const isToday = new Date().toDateString() === date.toDateString();
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const dayHeight = calculateDayHeight(dayShipments);
              
              // console.log(`📅 Day ${index}:`, {
              //   date: date.toDateString(),
              //   dayOfWeek: date.getDay(),
              //   isToday,
              //   isWeekend,
              //   shipmentsCount: dayShipments.length
              // });
              
              return (
                <div
                  key={index}
                  ref={isToday ? todayElementRef : null}
                  className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
                    isToday ? 'ring-2 ring-blue-500' : ''
                  } ${isWeekend ? 'bg-gray-50' : ''}`}
                  style={{ minHeight: `${Math.max(dayHeight, maxDayHeight)}px` }}
                >
                  {/* Day header */}
                  <div className={`p-3 border-b ${isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {dayNames[index]} {date.getDate()}/{date.getMonth() + 1}
                        </div>
                        <div className="text-xs text-gray-500">
                          {dayNamesShort[index]} {isToday && '(Hôm nay)'}
                        </div>
                      </div>
                      {dayShipments.length > 0 && (
                        <div className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                          {dayShipments.length}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Events */}
                   <div className="p-2 space-y-2">
                    {dayShipments.length > 0 ? (
                      dayShipments.map((shipment, shipmentIndex) => {
                        const pickupTime = formatTime(shipment.gionhan1, shipment.phutnhan1);
                        const deliveryTimes = getDeliveryTimes(shipment);
                        const customersDisplay = getCustomersDisplay(shipment);
                        const addressesDisplay = getAddressesDisplay(shipment);
                        const eventColors = getEventColors(shipment.trangthai);
                        const statusBadge = getStatusBadge(shipment.trangthai);
                        
                        return (
                          <div
                            key={shipmentIndex}
                            className={`p-2 rounded-lg cursor-pointer hover:shadow-md transition-shadow ${eventColors} relative`}
                            style={{ minHeight: '120px' }}
                            onClick={() => onSelectShipment && onSelectShipment(shipment)}
                          >
                            {/* Status Badge - Top Right */}
                            <div className="absolute top-2 right-2">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusBadge.color}`}>
                                {statusBadge.text}
                              </span>
                            </div>

                            {/* Hàng 1: Thời gian lấy - giao */}
                            <div className="flex items-center text-xs font-medium mb-1 pr-16">
                              <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {pickupTime ? pickupTime : '--:--'}
                                {deliveryTimes.length > 0 && ` → ${deliveryTimes.join(', ')}`}
                                {deliveryTimes.length === 0 && ' → --:--'}
                              </span>
                            </div>
                            
                            {/* Hàng 2: Khách hàng */}
                            <div className="text-xs font-medium mb-1 pr-16 truncate">
                              <span className="text-blue-600">KH:</span> {customersDisplay}
                            </div>
                            
                            {/* Hàng 3: Địa điểm lấy - giao */}
                            <div className="text-xs opacity-80 mb-1 pr-16">
                              <div className="flex items-start">
                                <MapPin className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                                <div className="min-h-0 flex-1">
                                  {addressesDisplay.map((addr, idx) => (
                                    <div key={idx} className="truncate text-xs">
                                      {addr}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Hàng 4: Biển số xe */}
                            <div className="text-xs font-bold text-green-600 pr-16 truncate">
                              🚛 {shipment.bienso || 'N/A'}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-xs text-gray-400 text-center mt-8">
                        Không có chuyến xe
                      </div>
                    )}
                  </div>

                </div>
              );
            })}
          </div>

          {/* Mobile: Single column with full-width cards */}
          <div className="md:hidden space-y-4">
            {weekDates.map((date, index) => {
              const dayShipments = getShipmentsForDate(date);
              const isToday = new Date().toDateString() === date.toDateString();
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              
              return (
                <div
                  key={index}
                  ref={isToday ? todayElementRef : null}
                  className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
                    isToday ? 'ring-2 ring-blue-500' : ''
                  } ${isWeekend ? 'bg-gray-50' : ''}`}
                >
                  {/* Day header */}
                  <div className={`p-4 border-b ${isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-base font-medium text-gray-900">
                          {dayNames[index]}, {date.getDate()}/{date.getMonth() + 1}
                        </div>
                        <div className="text-sm text-gray-500">
                          {dayNamesShort[index]} {isToday && '(Hôm nay)'}
                        </div>
                      </div>
                      {dayShipments.length > 0 && (
                        <div className="text-sm bg-blue-100 text-blue-600 px-3 py-1 rounded-full">
                          {dayShipments.length} chuyến
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Events */}
                  <div className="p-4">
                    {dayShipments.length > 0 ? (
                      <div className="space-y-3">
                        {dayShipments.map((shipment, shipmentIndex) => {
                          const pickupTime = formatTime(shipment.gionhan1, shipment.phutnhan1);
                          const deliveryTimes = getDeliveryTimes(shipment);
                          const customersDisplay = getCustomersDisplay(shipment);
                          const addressesDisplay = getAddressesDisplay(shipment);
                          const eventColors = getEventColors(shipment.trangthai);
                          const statusBadge = getStatusBadge(shipment.trangthai);
                          
                          return (
                            <div
                              key={shipmentIndex}
                              className={`p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow ${eventColors} relative`}
                              onClick={() => onSelectShipment && onSelectShipment(shipment)}
                            >
                              {/* Status Badge - Top Right */}
                              <div className="absolute top-3 right-3">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusBadge.color}`}>
                                  {statusBadge.text}
                                </span>
                              </div>

                              {/* Hàng 1: Thời gian lấy - giao */}
                              <div className="flex items-center text-sm font-medium mb-2 pr-20">
                                <Clock className="w-4 h-4 mr-1 flex-shrink-0" />
                                <span>
                                  {pickupTime ? pickupTime : '--:--'}
                                  {deliveryTimes.length > 0 && ` → ${deliveryTimes.join(', ')}`}
                                  {deliveryTimes.length === 0 && ' → --:--'}
                                </span>
                              </div>
                              
                              {/* Hàng 2: Khách hàng */}
                              <div className="text-sm font-medium mb-2 pr-20">
                                <span className="text-blue-600">Khách hàng:</span> {customersDisplay}
                              </div>
                              
                              {/* Hàng 3: Địa điểm lấy - giao */}
                              <div className="text-sm opacity-80 mb-2 pr-20">
                                <div className="flex items-start">
                                  <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    {addressesDisplay.map((addr, idx) => (
                                      <div key={idx} className="mb-1">
                                        {addr}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Hàng 4: Biển số xe */}
                              <div className="text-sm font-bold text-green-600 pr-20">
                                🚛 {shipment.bienso || 'N/A'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 text-sm">
                          Không có chuyến xe
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Create Shipment Button - Only for Dispatcher */}
      {userData?.type === USER_TYPES.DISPATCHER && (
        <button
          onClick={onCreateShipment}
          className="fixed bottom-24 right-4 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
          title="Tạo chuyến mới"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-24 left-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center justify-between">
            <span className="text-sm">{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyCalendarView;
